import React, {useCallback, useMemo, useState} from 'react';
import PageButton from '../elements/PageButton/PageButton';
import PasswordInput from './PasswordInput';
import {API_BASE} from '../../utils/apiConfig';
import {PASSWORD_REQUIREMENTS_HINT, validatePassword} from '../../configs/passwordPolicy';
import {usePageAnalytics} from '../../hooks/analytics/usePageAnalytics';
import './EmailAuthForm.css';

/**
 * Экран задания нового пароля — открывается по ссылке из письма
 * (/auth/reset/<токен>, см. constants/auth). Токен отправляется отсюда
 * POST-запросом: ручка не отвечает на GET, чтобы предзагрузчики писем не
 * «сжигали» одноразовую ссылку до того, как человек по ней перейдет.
 *
 * Успешная смена сразу впускает в игру — второй раз вводить тот же пароль
 * незачем.
 */
function PasswordResetPage({token, onAuthSuccess, onCancel}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isBusy, setIsBusy] = useState(false);

  usePageAnalytics('PasswordResetPage');

  // Почту здесь не знаем (в ссылке ее нет), поэтому проверка только по длине
  // и словарю — совпадение с адресом досмотрит сервер
  const passwordCheck = useMemo(() => validatePassword(password), [password]);

  const submit = useCallback(async (event) => {
    event.preventDefault();
    if (!passwordCheck.isValid) {
      setError(passwordCheck.error);
      return;
    }
    setError(null);
    setIsBusy(true);
    try {
      const response = await fetch(`${API_BASE}/auth/email/reset`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include',
        body: JSON.stringify({token, password}),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(data.error || 'Не удалось сменить пароль');
        return;
      }
      onAuthSuccess(data);
    } catch (requestError) {
      console.error('Password reset error', requestError);
      setError('Нет связи с сервером');
    } finally {
      setIsBusy(false);
    }
  }, [token, password, passwordCheck, onAuthSuccess]);

  return (
    <form className="email-auth-form email-auth-form_page" onSubmit={submit}>
      <h2 className="email-auth-title">Новый пароль</h2>
      <PasswordInput
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="new-password"
        placeholder="Новый пароль"
        disabled={isBusy}
        autoFocus
      />
      {!error && <p className="email-auth-hint">{PASSWORD_REQUIREMENTS_HINT}</p>}
      {error && <p className="email-auth-error">{error}</p>}
      <PageButton
        type="submit"
        disabled={isBusy || !password}
        pageId="PasswordResetPage"
        buttonId="reset-submit"
      >
        {isBusy ? 'Сохраняем...' : 'Сменить пароль'}
      </PageButton>
      <PageButton
        type="button"
        onClick={onCancel}
        pageId="PasswordResetPage"
        buttonId="reset-cancel"
      >
        Назад ко входу
      </PageButton>
    </form>
  );
}

export default PasswordResetPage;
